import { useRef } from "react";

/**
 * Inline animated teaching module — adds to (does not replace) the existing
 * lesson teaching stack of prose + handwritten note + interactive widget.
 *
 * Sits at the top of a lesson body so students get a guided motion-walkthrough
 * of the concept before exploring the interactive widget below.
 *
 * Renders an HTML5 <video> with native controls, sized full-width within the
 * lesson body (max 1080px), cream paper card to match the handwritten-note
 * aesthetic. Lazy-loaded; metadata only until interaction.
 */

export interface AnimatedExplainerProps {
  /** Path under /public, e.g. "/animations/music-theory/MT9-Circle-of-Fifths-Animated.mp4" */
  src: string;
  /** Short caption shown above the video, e.g. "The Circle of Fifths — built a fifth at a time". */
  caption: string;
  /** Optional one-line sub-caption under the title, e.g. "≈ 47 seconds · watch first". */
  hint?: string;
  /** Poster image path. Optional — if omitted, the first frame is shown. */
  poster?: string;
}

export function AnimatedExplainer({ src, caption, hint, poster }: AnimatedExplainerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  return (
    <figure className="my-6 mx-auto w-full max-w-[1080px] overflow-hidden rounded-2xl border border-amber-500/25 bg-[#fdfaf3] shadow-[0_8px_40px_rgba(0,0,0,0.35)]">
      <figcaption className="flex items-baseline justify-between gap-3 border-b border-amber-500/15 bg-[#f9f3e1]/80 px-5 py-3">
        <div className="flex items-baseline gap-3">
          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-700">
            ▶ Animated explainer
          </span>
          <span className="text-[15px] font-semibold text-zinc-800">{caption}</span>
        </div>
        {hint && <span className="text-[12px] italic text-zinc-600">{hint}</span>}
      </figcaption>
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        controls
        preload="metadata"
        playsInline
        className="block w-full bg-[#FBF7EE]"
        aria-label={caption}
      />
    </figure>
  );
}
